(() => {
  if (customElements.get('elshamy-promo-banner')) return;

  const storageKey = 'elshamy_product_coupon_banner_closed';

  class ElshamyPromoBanner extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      this.shell = this.closest('.elshamy-promo-shell');
      this.copyButton = this.querySelector('[data-promo-copy]');
      this.copyLabel = this.querySelector('[data-promo-copy-label]');
      this.status = this.querySelector('[data-promo-status]');
      this.originalLabel = this.copyLabel.textContent;
      this.editor = this.hasAttribute('data-design-mode') || Boolean(window.Shopify?.designMode);

      if (!this.editor && document.documentElement.hasAttribute('data-elshamy-promo-closed')) {
        this.shell.hidden = true;
        return;
      }

      this.copyButton.addEventListener('click', () => this.copyCode(), { signal: this.controller.signal });
      this.querySelector('[data-promo-close]')?.addEventListener('click', () => this.closeBanner(), { signal: this.controller.signal });
      if (this.dataset.autoApply === 'true' && !this.editor) this.applyDiscount();
    }

    disconnectedCallback() {
      this.controller?.abort();
      this.controller = null;
      clearTimeout(this.copyTimer);
      clearTimeout(this.closeTimer);
    }

    fallbackCopy() {
      const activeElement = document.activeElement;
      const field = document.createElement('textarea');
      field.value = this.dataset.code;
      field.className = 'elshamy-promo-fallback';
      field.setAttribute('readonly', '');
      document.body.appendChild(field);
      let copied = false;
      try {
        field.focus({ preventScroll: true });
        field.select();
        field.setSelectionRange(0, field.value.length);
        copied = document.execCommand('copy');
      } catch (error) {
        copied = false;
      } finally {
        field.remove();
        activeElement?.focus({ preventScroll: true });
      }
      return copied;
    }

    async copyCode() {
      if (this.copying) return;
      this.copying = true;
      clearTimeout(this.copyTimer);
      this.status.textContent = '';
      this.status.removeAttribute('data-error');
      let copied = false;
      try {
        if (navigator.clipboard?.writeText && window.isSecureContext) {
          await navigator.clipboard.writeText(this.dataset.code);
          copied = true;
        }
      } catch (error) { /* Clipboard permission denied: try the legacy fallback. */ }
      if (!copied) copied = this.fallbackCopy();
      this.copying = false;
      if (!this.isConnected) return;

      if (copied) {
        this.copyLabel.textContent = this.dataset.copiedLabel;
        this.status.textContent = this.dataset.copiedLabel;
        this.copyTimer = setTimeout(() => {
          this.copyLabel.textContent = this.originalLabel;
          this.status.textContent = '';
        }, 1800);
      } else {
        this.copyLabel.textContent = this.originalLabel;
        this.status.setAttribute('data-error', '');
        this.status.textContent = this.dataset.copyError;
        const selection = window.getSelection();
        const range = document.createRange();
        range.selectNodeContents(this.querySelector('.elshamy-promo-code'));
        selection?.removeAllRanges();
        selection?.addRange(range);
      }
    }

    closeBanner() {
      if (!this.editor) {
        try { sessionStorage.setItem(storageKey, '1'); } catch (error) { /* Session storage is optional. */ }
      }
      this.classList.add('is-closing');
      this.closeTimer = setTimeout(() => {
        this.shell.hidden = true;
        // Do not leave keyboard focus inside hidden content.
        const main = document.getElementById('MainContent');
        if (main && this.contains(document.activeElement)) main.focus({ preventScroll: true });
      }, window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 0 : 160);
    }

    async applyDiscount() {
      // Opt-in only. Preserve existing codes; never change cart items or quantities.
      const cartUrl = this.dataset.cartUrl;
      const signal = this.controller.signal;
      try {
        const response = await fetch(`${cartUrl}.js`, { credentials: 'same-origin', signal });
        if (!response.ok) return;
        const cart = await response.json();
        const codes = (cart.discount_codes || []).map((discount) => discount.code).filter(Boolean);
        if (codes.some((code) => code.toLowerCase() === this.dataset.code.toLowerCase())) return;
        codes.push(this.dataset.code);
        await fetch(`${cartUrl}/update.js`, {
          method: 'POST',
          credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ discount: codes.join(',') }),
          signal,
        });
      } catch (error) {
        // The visible code can still be copied and entered at checkout.
      }
    }
  }

  customElements.define('elshamy-promo-banner', ElshamyPromoBanner);
})();
